import type { CreateProjectInput, ProjectListInput, ProjectRepository, TenantContext, UpdateProjectInput } from '@zetyar/data-contracts'
import type { Project, UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

interface ProjectRow {
  readonly id: UUID
  readonly organization_id: UUID
  readonly title: string
  readonly description: string | null
  readonly status: string
  readonly priority: string
  readonly progress: number
  readonly start_date: string | null
  readonly target_date: string | null
  readonly completed_at: string | null
  readonly created_at: string
  readonly updated_at: string
  readonly responsible: { readonly id: UUID } | null
  readonly creator: { readonly id: UUID } | null
}

function mapProject(row: ProjectRow): Project {
  return {
    id: row.id,
    organizationId: row.organization_id,
    title: row.title,
    description: row.description,
    status: row.status as Project['status'],
    priority: row.priority as Project['priority'],
    progress: row.progress,
    responsibleId: row.responsible?.id ?? null,
    startDate: row.start_date,
    targetDate: row.target_date,
    completedAt: row.completed_at,
    createdBy: row.creator?.id ?? '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: null,
  }
}

export function createSupabaseProjectRepository(client: SupabaseClient): ProjectRepository {
  return {
    async listVisible(context: TenantContext, { limit }: ProjectListInput) {
      const { data, error } = await client
        .from('projects')
        .select(`
          id, organization_id, title, description, status, priority, progress,
          start_date, target_date, completed_at, created_at, updated_at,
          responsible:memberships!projects_responsible_membership_id_fkey (id),
          creator:memberships!projects_created_by_membership_id_fkey (id)
        `)
        .eq('organization_id', context.organizationId)
        .order('updated_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 100)))

      if (error) throw error
      return (data as unknown as ProjectRow[]).map(mapProject)
    },

    async create(context: TenantContext, input: CreateProjectInput) {
      const { data, error } = await client
        .rpc('create_project', {
          p_organization_id: context.organizationId,
          p_title: input.title,
          p_description: input.description ?? null,
          p_priority: input.priority ?? 'medium',
          p_responsible_membership_id: input.responsibleMembershipId ?? null,
          p_start_date: input.startDate ?? null,
          p_target_date: input.targetDate ?? null,
        })
        .single()

      if (error) throw error
      const row = data as Record<string, unknown>
      return mapProject({
        id: row.id as UUID,
        organization_id: row.organization_id as UUID,
        title: row.title as string,
        description: (row.description as string | null) ?? null,
        status: row.status as string,
        priority: row.priority as string,
        progress: row.progress as number,
        start_date: (row.start_date as string | null) ?? null,
        target_date: (row.target_date as string | null) ?? null,
        completed_at: (row.completed_at as string | null) ?? null,
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
        responsible: row.responsible_membership_id ? { id: row.responsible_membership_id as UUID } : null,
        creator: { id: row.created_by_membership_id as UUID },
      })
    },

    async update(context: TenantContext, projectId: UUID, input: UpdateProjectInput) {
      const { error } = await client.rpc('update_project', {
        p_id: projectId,
        p_organization_id: context.organizationId,
        p_title: input.title ?? null,
        p_description: input.description ?? null,
        p_status: input.status ?? null,
        p_priority: input.priority ?? null,
        p_progress: input.progress ?? null,
        p_responsible_membership_id: input.responsibleMembershipId ?? null,
        p_start_date: input.startDate ?? null,
        p_target_date: input.targetDate ?? null,
      })
      if (error) throw error
    },

    async delete(context: TenantContext, projectId: UUID) {
      const { error } = await client.rpc('delete_project', { p_id: projectId, p_organization_id: context.organizationId })
      if (error) throw error
    },
  }
}
